import { normalizeTitleContributors } from '@/utils/title-contributors'

interface JsonApiResource {
  id: string
  type: string
  attributes: Record<string, any>
  relationships?: Record<string, {
    data: { id: string; type: string } | { id: string; type: string }[] | null
  }>
}

interface JsonApiResponse {
  data: JsonApiResource | JsonApiResource[]
  included?: JsonApiResource[]
  meta?: Record<string, any>
  links?: Record<string, string>
}

interface TransformedResponse<T> {
  data: T;
  meta?: Record<string, any>;
}

/**
 * Transform a JSON API response to a normalized format
 */
export class JsonApiTransformer {
  private included: JsonApiResource[] = []

  transform<T>(response: any): TransformedResponse<T> {
    this.included = response.included || []
    
    const result: TransformedResponse<T> = {
      data: Array.isArray(response.data) 
        ? response.data.map((item: any) => this.transformItem(item))
        : this.transformItem(response.data)
    }
    
    if (response.meta) {
      result.meta = response.meta
    }
    
    return result
  }

  private isInactiveChapter(resource: any): boolean {
    if (!resource) return false
    if (resource.type !== 'chapters') return false
    const status = resource.status
    return typeof status === 'string' && status.toLowerCase() === 'inactive'
  }

  private transformItem(item: any) {
    if (!item) return null

    const transformed: any = {
      id: item.id,
      type: item.type
    }

    // Transform attributes
    if (item.attributes) {
      Object.entries(item.attributes).forEach(([key, value]) => {
        const camelKey = this.toCamelCase(key)
        transformed[camelKey] = this.transformValue(value)
      })
    }

    // Preserve per-resource meta (e.g. meta.seo with og_image for social sharing)
    if (item.meta) {
      transformed.meta = item.meta
    }

    // Transform relationships
    if (item.relationships) {
      Object.entries(item.relationships).forEach(([key, value]: [string, any]) => {
        const camelKey = this.toCamelCase(key)
        
        // Handle array relationships (like children)
        if (Array.isArray(value.data)) {
          transformed[camelKey] = value.data.map((relatedItem: any) => {
            const includedItem = this.findIncludedItem(relatedItem.id, relatedItem.type)
            const transformedRelated = includedItem ? this.transformItem(includedItem) : relatedItem
            if (this.isInactiveChapter(transformedRelated)) return null
            return this.applyRelationshipMeta(transformedRelated, relatedItem)
          }).filter(Boolean)
        } else if (value.data === null) {
          transformed[camelKey] = null
        } else if (value.data) {
          // Find the related item in the included array
          const includedItem = this.findIncludedItem(value.data.id, value.data.type)
          const transformedRelated = includedItem ? this.transformItem(includedItem) : value.data
          if (this.isInactiveChapter(transformedRelated)) {
            transformed[camelKey] = null
          } else {
            transformed[camelKey] = this.applyRelationshipMeta(transformedRelated, value.data)
          }
        }
      })
    }

    if (transformed.type === 'titles') {
      return normalizeTitleContributors(transformed)
    }

    return transformed
  }

  private findIncludedItem(id: string, type: string): JsonApiResource | undefined {
    return this.included.find(item => item.id === id && item.type === type)
  }

  /**
   * Merge JSON:API relationship linkage meta (e.g. people pivot role/order)
   * onto the resolved related resource.
   */
  private applyRelationshipMeta(related: any, linkage: any): any {
    if (!related || !linkage?.meta || typeof linkage.meta !== 'object') {
      return related
    }

    const meta = linkage.meta as Record<string, unknown>
    const withMeta = { ...related }

    for (const [key, value] of Object.entries(meta)) {
      const camelKey = this.toCamelCase(key)
      if (withMeta[camelKey] == null && value != null) {
        withMeta[camelKey] = this.transformValue(value)
      }
    }

    return withMeta
  }

  /**
   * Recursively camelCase keys in plain objects/arrays (e.g. nested subscription_details).
   */
  private transformValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.transformValue(item))
    }

    if (value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
      return this.keysToCamelCase(value as Record<string, unknown>)
    }

    return value
  }

  private keysToCamelCase(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      result[this.toCamelCase(key)] = this.transformValue(value)
    }
    return result
  }

  private toCamelCase(str: string): string {
    return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase())
  }
} 